import React, { useCallback, useState, createContext, useContext } from 'react';
interface CheckoutContextType {
  openUpsell: () => void;
}
const CheckoutContext = createContext<CheckoutContextType | null>(null);
interface CheckoutProviderProps {
  children: ReactNode;
  mainVariantId: string;
  upsellVariantId: string;
  cartBaseUrl: string;
}
export function CheckoutProvider({
  children,
  mainVariantId,
  upsellVariantId,
  cartBaseUrl
}: CheckoutProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState<'accept' | 'decline' | null>(null);
  const openUpsell = useCallback(() => {
    if (loading) return;
    setIsOpen(true);
  }, [loading]);
  const close = useCallback(() => {
    if (loading) return;
    setIsOpen(false);
  }, [loading]);
  const handleAccept = useCallback(() => {
    if (loading) return;
    setLoading('accept');
    window.location.href = `${cartBaseUrl}${mainVariantId}:1,${upsellVariantId}:1`;
  }, [loading, mainVariantId, upsellVariantId, cartBaseUrl]);
  const handleDecline = useCallback(() => {
    if (loading) return;
    setLoading('decline');
    window.location.href = `${cartBaseUrl}${mainVariantId}:1`;
  }, [loading, mainVariantId, cartBaseUrl]);
  return <CheckoutContext.Provider value={{
    openUpsell
  }} data-id="element-40">
      {children}
      {isOpen && <UpsellModal onAccept={handleAccept} onDecline={handleDecline} onClose={close} loading={loading} data-id="element-41" />}
    </CheckoutContext.Provider>;
}
export function useCheckout() {
  const ctx = useContext(CheckoutContext);
  if (!ctx) throw new Error('useCheckout must be used within CheckoutProvider');
  return ctx;
}
interface UpsellModalProps {
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
  loading: 'accept' | 'decline' | null;
}
function UpsellModal({
  onAccept,
  onDecline,
  onClose,
  loading
}: UpsellModalProps) {
  const benefits = ['Fortalece tu autoestima', 'Deja de depender de la aprobación externa', 'Aprende a poner límites sin culpa', 'Incluye un plan práctico de 30 días'];
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} role="dialog" aria-modal="true" aria-labelledby="upsell-title" data-id="element-42">
      <div className="relative w-full max-w-md bg-gradient-to-b from-[#1a0f2e] to-[#0a0514] border border-brand-gold/40 rounded-2xl shadow-2xl shadow-brand-gold/10 animate-slideUp overflow-hidden max-h-[95vh] overflow-y-auto" data-id="element-43">
        {/* Decorative gold gradient overlay */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" data-id="element-44"></div>

        {/* Close X */}
        <button onClick={onClose} disabled={loading !== null} aria-label="Cerrar" className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-brand-gray hover:text-brand-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed z-10 bg-black/40 backdrop-blur-sm" data-id="element-45">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-46">
            <line x1="18" y1="6" x2="6" y2="18" data-id="element-47"></line>
            <line x1="6" y1="6" x2="18" y2="18" data-id="element-48"></line>
          </svg>
        </button>

        <div className="p-6 sm:p-8" data-id="element-49">
          {/* Tag */}
          <div className="inline-block px-3 py-1 rounded-full border border-brand-gold/40 bg-brand-gold/10 text-[10px] tracking-widest text-brand-gold font-semibold mb-4" data-id="element-50">
            OFERTA EXCLUSIVA
          </div>

          {/* Product preview: image + title side by side */}
          <div className="flex items-start gap-3 sm:gap-4 mb-4" data-id="element-51">
            <img src="https://cdn.magicpatterns.com/uploads/uNsuirT4mDGweYqfufJesy/ChatGPT_Image_1_jun_2026,_11_52_51.png" alt="Amor Propio y Autoestima Inquebrantable" className="w-20 sm:w-24 rounded-md shadow-lg shadow-black/50 border border-brand-gold/20 flex-shrink-0" data-id="element-52" />
            <div className="flex-1 min-w-0" data-id="element-53">
              <h2 id="upsell-title" className="font-serif text-xl sm:text-2xl font-bold leading-tight text-white mb-1" data-id="element-54">
                Completa tu transformación interior
              </h2>
              <p className="text-xs sm:text-sm text-brand-gold font-semibold leading-snug" data-id="element-55">
                Amor Propio y Autoestima Inquebrantable
              </p>
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-sm text-brand-gray mb-4 leading-relaxed" data-id="element-56">
            Fortalecé la base emocional que sostiene tu nueva realidad.
          </p>

          {/* Benefits */}
          <ul className="space-y-2 mb-5" data-id="element-57">
            {benefits.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-brand-white" data-id="element-58">
                <span className="text-brand-gold mt-0.5 flex-shrink-0" data-id="element-59">✓</span>
                <span data-id="element-60">{b}</span>
              </li>)}
          </ul>

          {/* Price */}
          <div className="bg-black/40 border border-brand-gold/30 rounded-lg p-3 mb-5 text-center" data-id="element-61">
            <p className="text-xs text-brand-gray mb-0.5" data-id="element-62">Solo por hoy</p>
            <p className="text-brand-gold text-2xl sm:text-3xl font-bold leading-tight" data-id="element-63">
              $5.990
            </p>
          </div>

          {/* Primary CTA */}
          <button onClick={onAccept} disabled={loading !== null} className="w-full bg-brand-gold text-brand-black px-6 py-3.5 rounded-lg text-sm sm:text-base font-bold hover:opacity-90 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 mb-2 flex items-center justify-center gap-2" data-id="element-64">
            {loading === 'accept' ? <>
                <Spinner data-id="element-65" /> Procesando...
              </> : 'Sí, agregar por $5.990'}
          </button>

          {/* Secondary CTA */}
          <button onClick={onDecline} disabled={loading !== null} className="w-full bg-white/5 text-brand-white border border-brand-gold/40 px-6 py-3.5 rounded-lg text-sm sm:text-base font-semibold hover:bg-white/10 hover:border-brand-gold/70 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-66">
            {loading === 'decline' ? <>
                <Spinner data-id="element-67" /> Procesando...
              </> : 'No gracias, continuar'}
          </button>
        </div>
      </div>
    </div>;
}
function Spinner() {
  return <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" data-id="element-68">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" data-id="element-69"></circle>
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-70"></path>
    </svg>;
}